import React, { useEffect, useRef, useState } from 'react'
import { Card } from 'react-bootstrap';
import { Control, Controller, DeepMap, FieldError, useForm } from 'react-hook-form';
import { RoomMaintenanceFilter, RoomMaintenanceFilterSchema } from '../schema/RoomMaintenanceFilterSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import Select from 'react-select';
import useGetAllRoomType from '../../../services/room-type/hooks/useGetAllRoomType';
import { DateTypeFilterRoomMaintenance } from '../types/DateTypeFilterRoomMaintenance';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { DateRange, DateRangePicker, RangeKeyDict } from 'react-date-range';
import { format } from 'date-fns';

interface RoomMaintenanceFilterProps {
    onFilterSubmit: (data: RoomMaintenanceFilter) => void;
}

const RoomMaintenanceFilterComponent: React.FC<RoomMaintenanceFilterProps> = ({ onFilterSubmit }) => {
    const isMobile = useMediaQuery('(max-width: 768px)');
    const { handleSubmit, control, watch, formState: { errors }, reset, setValue } = useForm<RoomMaintenanceFilter>({
        resolver: zodResolver(RoomMaintenanceFilterSchema),
        defaultValues: {
            room_type_id: 'all',
            room_id: 'all',
            date_type: 'all',
            gte: null,
            lte: null,
        },
    });

    const [roomTypeSelected, setRoomTypeSelected] = useState<any>(null);

    // State for DateRangePicker
    const [dateRange, setDateRange] = useState([
        {
            startDate: new Date(),
            endDate: new Date(),
            key: 'selection'
        }
    ]);
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const calendarRef = useRef<HTMLDivElement>(null);

    // Watch form values to display them
    const gteDate = watch('gte');
    const lteDate = watch('lte');
    const dateType = watch('date_type');

    const handleFilterReset = () => {
        reset({
            room_type_id: 'all',
            room_id: 'all',
            date_type: 'all',
            gte: null,
            lte: null,
        });
        setRoomTypeSelected(null);
        // Reset date range picker state
        setDateRange([{ startDate: new Date(), endDate: new Date(), key: 'selection' }]);
    }

    const onSubmit = (data: RoomMaintenanceFilter) => {
        onFilterSubmit(data); // Call parent function
    };

    const handleDateChange = (rangesByKey: RangeKeyDict) => {
        const { selection } = rangesByKey;
        if (selection.startDate && selection.endDate) {
            setDateRange([{
                startDate: selection.startDate,
                endDate: selection.endDate,
                key: selection.key || 'selection'
            }]);
            // Update react-hook-form values programmatically
            setValue('gte', format(selection.startDate, 'yyyy-MM-dd'), { shouldValidate: true });
            setValue('lte', format(selection.endDate, 'yyyy-MM-dd'), { shouldValidate: true });
        }
    };

    // Effect to handle clicks outside the calendar
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
                setIsCalendarOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [calendarRef]);

    const calendarContainerStyle: React.CSSProperties = {
        position: 'absolute',
        zIndex: 1000,
        top: '100%',
        marginTop: '8px',
        border: '1px solid #dfe2e5',
        borderRadius: '8px',
        boxShadow: '0 6px 15px rgba(0, 0, 0, 0.15)',
        right: 0
    };

    if (isMobile) {
        calendarContainerStyle.left = '50%';
        calendarContainerStyle.transform = 'translateX(-50%)';
        calendarContainerStyle.right = 'auto';
    }

    return (
        <Card className="mb-3 shadow-none">
            <Card.Body>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="row align-items-end">
                        <div className="col-md-3 mt-2 mt-md-0">
                            <RoomTypeComponent control={control} errors={errors} setRoomTypeSelected={setRoomTypeSelected} />
                        </div>
                        <div className="col-md-3 mt-2 mt-md-0">
                            {watch('room_type_id') !== undefined && (
                                <RoomComponent control={control} errors={errors} roomTypeSelected={roomTypeSelected} />
                            )}
                        </div>
                        <div className='col-md-3 mt-2 mt-md-0'>
                            <div className="form-group">
                                <label htmlFor="date_type" className="mb-1">Date Type</label>
                                <Controller
                                    name='date_type'
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            options={DateTypeFilterRoomMaintenance}
                                            value={DateTypeFilterRoomMaintenance.find((option) => option.value === field.value)}
                                            onChange={(selectedOption) => field.onChange(selectedOption?.value || 'all')}
                                            defaultValue={DateTypeFilterRoomMaintenance.find((option) => option.value === 'all')}
                                        />
                                    )}
                                />
                                {errors.date_type && <small className="text-danger">{errors.date_type.message}</small>}
                            </div>
                        </div>
                        <div className='col-md-3 mt-2 mt-md-0'>
                            <div className="form-group">
                                <label htmlFor="date" className="mb-1">Pick Date Range</label>
                                <div className="position-relative" ref={calendarRef}>
                                    <div className="input-group">
                                        <input
                                            type='text'
                                            readOnly
                                            className='form-control'
                                            onClick={() => dateType !== 'all' && setIsCalendarOpen(!isCalendarOpen)}
                                            value={gteDate && lteDate ? `${format(new Date(gteDate), 'dd MMM yyyy')} - ${format(new Date(lteDate), 'dd MMM yyyy')}` : ''}
                                            placeholder="Select date range"
                                            disabled={dateType === 'all'}
                                        />
                                        <span className="input-group-text" onClick={() => dateType !== 'all' && setIsCalendarOpen(!isCalendarOpen)} style={{ cursor: dateType !== 'all' ? 'pointer' : 'not-allowed' }}>
                                            <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-calendar-event" width="20" height="20" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                                <path d="M4 5m0 2a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2z"></path>
                                                <path d="M16 3l0 4"></path>
                                                <path d="M8 3l0 4"></path>
                                                <path d="M4 11l16 0"></path>
                                                <path d="M8 15h2v2h-2z"></path>
                                            </svg>
                                        </span>
                                    </div>
                                    {isCalendarOpen && (
                                        <div style={calendarContainerStyle}>
                                            {isMobile ? (
                                                <DateRange
                                                    onChange={handleDateChange}
                                                    moveRangeOnFirstSelection={false}
                                                    ranges={dateRange}
                                                    months={1}
                                                    direction="vertical"
                                                    className="shadow-sm"
                                                />
                                            ) : (
                                                <DateRangePicker
                                                    onChange={handleDateChange}
                                                    moveRangeOnFirstSelection={false}
                                                    ranges={dateRange}
                                                    months={2}
                                                    direction="horizontal"
                                                    inputRanges={[]}
                                                    staticRanges={[]}
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row mt-3">
                        <div className="col-md-3 mt-2 mt-md-0">
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={handleFilterReset}
                            >
                                Reset Filter
                            </button>
                            <button type='submit' className="btn btn-primary btn-sm ms-2">
                                Apply Filter
                            </button>
                        </div>
                    </div>
                </form>
            </Card.Body>
        </Card>
    )
}

export default RoomMaintenanceFilterComponent;

interface RoomTypeComponentProps {
    control: Control<RoomMaintenanceFilter>;
    errors: DeepMap<RoomMaintenanceFilter, FieldError>;
    setRoomTypeSelected?: (roomType: any) => void;
}

const RoomTypeComponent: React.FC<RoomTypeComponentProps> = ({ control, errors, setRoomTypeSelected }) => {
    const defaultOptions = [{ value: 'all', label: 'All' }];
    const [roomTypeOptions, setRoomTypeOptions] = useState(defaultOptions);
    const { data: roomTypeData, isSuccess, isFetching, isError } = useGetAllRoomType();

    useEffect(() => {
        if (isSuccess && roomTypeData) {
            const options = roomTypeData.data.map((roomType) => ({
                value: roomType.id,
                label: roomType.name,
            }));
            setRoomTypeOptions([...defaultOptions, ...options]);
        }
    }, [isSuccess, roomTypeData]);

    return (
        <div className="form-group">
            {isError && <small className="text-danger">Failed to fetch room types</small>}
            {isFetching && (
                <>
                    <label htmlFor='room_type_id' className='mb-1'>Room</label>
                    <Select
                        options={defaultOptions}
                        isLoading={true}
                    />
                </>
            )}
            {isSuccess && !isFetching && (
                <>
                    <label htmlFor="room_type_id" className="mb-1">Room</label>
                    <Controller
                        name='room_type_id'
                        control={control}
                        render={({ field }) => (
                            <Select
                                {...field}
                                options={roomTypeOptions}
                                value={roomTypeOptions.find((option) => option.value === field.value)}
                                onChange={(selectedOption) => {
                                    field.onChange(selectedOption?.value || 'all');
                                    setRoomTypeSelected && setRoomTypeSelected(roomTypeData.data.find((roomType: any) => roomType.id === selectedOption?.value));
                                }}
                                defaultValue={roomTypeOptions.find((option) => option.value === 'all')}
                            />
                        )}
                    />
                    {errors.room_type_id && <small className="text-danger">{errors.room_type_id.message}</small>}
                </>
            )}
        </div>
    )
}

interface RoomComponentProps {
    control: Control<RoomMaintenanceFilter>;
    errors: DeepMap<RoomMaintenanceFilter, FieldError>;
    roomTypeSelected: any;
}

const RoomComponent: React.FC<RoomComponentProps> = ({ control, errors, roomTypeSelected }) => {
    const defaultOptions = [{ value: 'all', label: 'All' }];
    const [roomOptions, setRoomOptions] = useState(defaultOptions);

    useEffect(() => {
        if (roomTypeSelected) {
            setRoomOptions([...defaultOptions, ...roomTypeSelected.rooms.map((room: any) => ({
                value: room.id,
                label: room.room_number,
            }))]);
        }
    }, [roomTypeSelected]);

    return (
        <div className="form-group">
            <label htmlFor="room_id" className="mb-1">Sub Room</label>
            <Controller
                name='room_id'
                control={control}
                render={({ field }) => (
                    <Select
                        {...field}
                        options={roomOptions}
                        value={roomOptions.find((option) => option.value === field.value)}
                        onChange={(selectedOption) => field.onChange(selectedOption?.value || 'all')}
                        isDisabled={!roomTypeSelected}
                        defaultValue={roomOptions.find((option) => option.value === 'all')}
                    />
                )}
            />
            {errors.room_id && <small className="text-danger">{errors.room_id.message}</small>}
        </div>
    );
};